import React from 'react';
import { AppProvider } from '@toolpad/core/AppProvider';
import { CustomDashboardLayout } from '../components/layout/CustomDashboardLayout';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Import our configuration and theme
import { NAVIGATION, BRANDING } from '../config/navigation';
import type { VRPAppProviderProps, Router } from '../types/toolpad';
import { DecoyLogo } from '../components/branding/DecoyLogo';
import { VRPUnifiedProvider } from '../contexts/VRPUnifiedProvider';

/**
 * VRP-specific AppProvider wrapper that integrates MUI Toolpad with React Router
 * and provides Supabase theming
 * 
 * Features:
 * - React Router integration
 * - Supabase theme with CSS variables
 * - Automatic breadcrumb generation
 * - Navigation state management
 * - Responsive layout with dual sidebars
 * - VRP context providers (Auth, Data, UI, Notifications)
 * 
 * Provider hierarchy:
 * - AppProvider (MUI Toolpad) - uses theme from parent DomainThemeProvider
 * - VRPUnifiedProvider (VRP contexts)
 * - CustomDashboardLayout (with SmartSecondarySidebar)
 */

/**
 * Hook to create router object for Toolpad from React Router
 */
function useToolpadRouter(): Router {
  const location = useLocation();
  const navigate = useNavigate();

  return React.useMemo(() => ({
    pathname: location.pathname,
    searchParams: new URLSearchParams(location.search),
    navigate: (url: string | URL, options?: { history?: 'auto' | 'push' | 'replace' }) => {
      const urlString = typeof url === 'string' ? url : url.toString();
      const historyMethod = options?.history || 'push';
      
      if (historyMethod === 'replace') {
        navigate(urlString, { replace: true });
      } else {
        navigate(urlString);
      }
    },
  }), [location.pathname, location.search, navigate]);
}

/**
 * VRP Toolpad App Provider Component
 * Wraps the application with Toolpad AppProvider and provides VRP-specific configuration
 */
export function VRPToolpadAppProvider({ 
  children,
  navigation = NAVIGATION,
  branding = BRANDING,
  session,
  authentication,
  window: windowObject,
}: Omit<VRPAppProviderProps, 'theme'>) {
  const router = useToolpadRouter();
  
  // Get theme from parent DomainThemeProvider context
  const theme = useTheme();

  // Enhanced branding with DECOY logo
  const decoyBranding = React.useMemo(() => ({
    ...branding,
    logo: <DecoyLogo size="header" />,
  }), [branding]);

  return (
    <>
      <CssBaseline />
      <AppProvider
        navigation={navigation}
        router={router}
        theme={theme}
        branding={decoyBranding}
        session={session}
        authentication={authentication}
        window={windowObject}
      >
        <VRPUnifiedProvider>
          <CustomDashboardLayout>
            {children}
          </CustomDashboardLayout>
        </VRPUnifiedProvider>
      </AppProvider>
    </>
  );
}

/**
 * VRP App Provider without DashboardLayout
 * Use this when you want to handle layout manually or for specific pages
 */
export function VRPToolpadProvider({ 
  children,
  navigation = NAVIGATION,
  branding = BRANDING,
  session,
  authentication,
  window: windowObject,
}: Omit<VRPAppProviderProps, 'theme'>) {
  const router = useToolpadRouter();
  
  // Get theme from parent DomainThemeProvider context
  const theme = useTheme();

  return (
    <>
      <CssBaseline />
      <AppProvider
        navigation={navigation}
        router={router}
        theme={theme}
        branding={branding}
        session={session}
        authentication={authentication}
        window={windowObject}
      >
        {children}
      </AppProvider>
    </>
  );
}

/**
 * Hook to access Toolpad navigation state within VRP system
 * Enhanced with robust URL parsing and validation
 */
export function useVRPToolpadNavigation() {
  const router = useToolpadRouter();
  
  // Add null safety for when Toolpad context isn't available yet
  if (!router) {
    console.warn('[useVRPToolpadNavigation] Toolpad router not available, returning default state');
    return {
      navigation: {
        currentProject: null,
        currentScenario: null,
        currentDataset: null,
        currentFeature: 'dashboard',
        pathname: '/',
        parseError: 'Router not available',
        hasValidProject: false,
        hasValidScenario: false,
        hasValidDataset: false,
      },
      router: null,
      actions: {
        navigateToProject: () => console.warn('Navigation not available'),
        navigateToScenario: () => console.warn('Navigation not available'),
        navigateToDataset: () => console.warn('Navigation not available'),
        navigateToFeature: () => console.warn('Navigation not available'),
        navigateToTableEditor: () => console.warn('Navigation not available'),
        navigateToDashboard: () => console.warn('Navigation not available'),
        updateSearchParams: () => console.warn('Navigation not available'),
        refreshNavigationState: () => console.warn('Navigation not available'),
      },
    };
  }
  
  // Enhanced URL parsing with validation and error handling
  const navigationState = React.useMemo(() => {
    const pathSegments = router.pathname.split('/').filter(Boolean);
    
    // Initialize navigation state
    let currentProject: string | null = null;
    let currentScenario: string | null = null;
    let currentDataset: string | null = null;
    let currentFeature: string = 'dashboard';
    let parseError: string | null = null;
    
    try {
      // Enhanced route pattern matching
      if (pathSegments.length === 0) {
        currentFeature = 'dashboard';
      } else {
        currentFeature = pathSegments[0];
        
        // Project-based routes: /projects/:projectId/...
        if (pathSegments[0] === 'projects' && pathSegments.length >= 2) {
          const projectIdRaw = pathSegments[1];
          
          // Validate project ID format (should be numeric)
          if (!/^\d+$/.test(projectIdRaw)) {
            parseError = `Invalid project ID format: ${projectIdRaw}`;
          } else {
            currentProject = projectIdRaw;
            
            // Look for scenario context: /projects/:projectId/scenarios/:scenarioId
            const scenarioIndex = pathSegments.findIndex(segment => segment === 'scenarios');
            if (scenarioIndex !== -1 && pathSegments.length > scenarioIndex + 1) {
              const scenarioIdRaw = pathSegments[scenarioIndex + 1];
              
              // Validate scenario ID format
              if (!/^\d+$/.test(scenarioIdRaw)) {
                parseError = `Invalid scenario ID format: ${scenarioIdRaw}`;
              } else {
                currentScenario = scenarioIdRaw;
                
                // Look for dataset context: .../datasets/:datasetId
                const datasetIndex = pathSegments.findIndex(segment => segment === 'datasets');
                if (datasetIndex !== -1 && pathSegments.length > datasetIndex + 1) {
                  const datasetIdRaw = pathSegments[datasetIndex + 1];
                  
                  // Validate dataset ID format
                  if (!/^\d+$/.test(datasetIdRaw)) {
                    parseError = `Invalid dataset ID format: ${datasetIdRaw}`;
                  } else {
                    currentDataset = datasetIdRaw;
                  }
                }
              }
            }
          }
        }
        
        // Table Editor routes: /table-editor/:tableType
        else if (pathSegments[0] === 'table-editor') {
          currentFeature = 'table-editor';
          // Table type is available in pathSegments[1] if needed
        }
        
        // Other feature routes
        else if (['dashboard', 'settings', 'tools'].includes(pathSegments[0])) {
          currentFeature = pathSegments[0];
        }
      }
    } catch (error) {
      console.error('[useVRPToolpadNavigation] URL parsing error:', error);
      parseError = `URL parsing failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
    
    return {
      currentProject,
      currentScenario,
      currentDataset,
      currentFeature,
      pathname: router.pathname,
      searchParams: router.searchParams,
      parseError,
      // Navigation context validation flags
      hasValidProject: currentProject !== null && !parseError,
      hasValidScenario: currentScenario !== null && !parseError,
      hasValidDataset: currentDataset !== null && !parseError,
    };
  }, [router.pathname, router.searchParams]);

  const actions = React.useMemo(() => ({
    navigateToProject: (projectId: string | number) => {
      const validProjectId = String(projectId);
      if (!/^\d+$/.test(validProjectId)) {
        console.error('[useVRPToolpadNavigation] Invalid project ID:', projectId);
        return;
      }
      router.navigate(`/projects/${validProjectId}`);
    },
    navigateToScenario: (projectId: string | number, scenarioId: string | number) => {
      const validProjectId = String(projectId);
      const validScenarioId = String(scenarioId);
      if (!/^\d+$/.test(validProjectId) || !/^\d+$/.test(validScenarioId)) {
        console.error('[useVRPToolpadNavigation] Invalid IDs:', { projectId, scenarioId });
        return;
      }
      router.navigate(`/projects/${validProjectId}/scenarios/${validScenarioId}`);
    },
    navigateToDataset: (projectId: string | number, scenarioId: string | number, datasetId: string | number) => {
      const validProjectId = String(projectId);
      const validScenarioId = String(scenarioId);
      const validDatasetId = String(datasetId);
      if (!/^\d+$/.test(validProjectId) || !/^\d+$/.test(validScenarioId) || !/^\d+$/.test(validDatasetId)) {
        console.error('[useVRPToolpadNavigation] Invalid IDs:', { projectId, scenarioId, datasetId });
        return;
      }
      router.navigate(`/projects/${validProjectId}/scenarios/${validScenarioId}/datasets/${validDatasetId}`);
    },
    navigateToFeature: (feature: string) => {
      // Validate feature is a known route
      const validFeatures = ['dashboard', 'projects', 'table-editor', 'settings', 'tools'];
      if (!validFeatures.includes(feature)) {
        console.error('[useVRPToolpadNavigation] Unknown feature:', feature);
        return;
      }
      router.navigate(`/${feature}`);
    },
    navigateToTableEditor: (tableType?: string) => {
      const basePath = '/table-editor';
      if (tableType) {
        router.navigate(`${basePath}/${tableType}`);
      } else {
        router.navigate(basePath);
      }
    },
    navigateToDashboard: () => {
      router.navigate('/dashboard');
    },
    updateSearchParams: (params: Record<string, string>) => {
      try {
        const newSearchParams = new URLSearchParams(router.searchParams);
        Object.entries(params).forEach(([key, value]) => {
          if (value && value.trim()) {
            newSearchParams.set(key, value);
          } else {
            newSearchParams.delete(key);
          }
        });
        const queryString = newSearchParams.toString();
        const newUrl = queryString ? `${router.pathname}?${queryString}` : router.pathname;
        router.navigate(newUrl);
      } catch (error) {
        console.error('[useVRPToolpadNavigation] Failed to update search params:', error);
      }
    },
    // Navigation state refresh trigger for data providers
    refreshNavigationState: () => {
      // This will trigger a re-evaluation of the navigation state
      // Useful when data providers need to sync with navigation changes
      console.log('[useVRPToolpadNavigation] Navigation state refresh triggered');
    },
  }), [router]);

  // Debug navigation state changes
  React.useEffect(() => {
    console.log('[useVRPToolpadNavigation] Navigation state updated:', {
      currentProject: navigationState.currentProject,
      currentScenario: navigationState.currentScenario, 
      currentDataset: navigationState.currentDataset,
      currentFeature: navigationState.currentFeature,
      pathname: navigationState.pathname,
      parseError: navigationState.parseError,
      hasValidProject: navigationState.hasValidProject,
      hasValidScenario: navigationState.hasValidScenario,
      hasValidDataset: navigationState.hasValidDataset,
    });
  }, [navigationState]);

  return {
    navigation: navigationState,
    router,
    actions,
  };
}

/**
 * Provider for VRP authentication context
 * Placeholder for future authentication implementation
 */
export function useVRPAuthentication() {
  // TODO: Implement Supabase authentication integration
  return {
    user: null,
    signIn: () => {
      console.log('Sign in functionality to be implemented');
    },
    signOut: () => {
      console.log('Sign out functionality to be implemented');
    },
  };
}

/**
 * Helper component for pages that need to display outside the main dashboard layout
 * Useful for login pages, error pages, etc.
 */
export function VRPPageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <VRPToolpadProvider>
      {children}
    </VRPToolpadProvider>
  );
}

/**
 * Helper hook to get current page title based on navigation
 */
export function usePageTitle(): string {
  const { navigation } = useVRPToolpadNavigation();
  
  React.useEffect(() => {
    // Extract title from current feature
    let title = 'VRP System';
    
    if (navigation.currentProject) {
      title = `Project ${navigation.currentProject} - VRP System`;
    } else {
      // Capitalize first letter of feature
      const featureTitle = navigation.currentFeature.charAt(0).toUpperCase() + 
                          navigation.currentFeature.slice(1);
      title = `${featureTitle} - VRP System`;
    }
    
    // Update document title
    document.title = title;
    
    return () => {
      document.title = 'VRP System';
    };
  }, [navigation]);

  return document.title;
}

export default VRPToolpadAppProvider;